import React, { useMemo, useState, Fragment } from 'react';
import moment from 'moment';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Button from '../../Button';
import ShowMore from '../../common/ShowMore';
import { useProgramConsents } from '../hooks';
import SimpleAvatar from '../../common/SimpleAvatar';
import ProgramSubscription from '../ProgramSubscription';
import { SUBSCRIPTION_STATUS } from '../../../constants';
import ExpandableListItem from '../../ExpandableListItem';
import { viewProgramCost } from '../utils/viewProgramCost';
import ActionConfirmation from '../../common/ActionConfirmation';
import {
  loggedUserIdSelector,
  targetUserSelector,
} from '../../../redux/context/selectors';
import { MEMBERSHIP_TYPE, PROGRAM_TYPE } from '../../../constants/programs';
import useProgramSubscription from '../../../hooks/program/useProgramSubscription';
import { actions as leaveProgramActions } from '../../../redux/programs/leaveProgram';
import { actions as acceptInvitationActions } from '../../../redux/programs/acceptInvitation';
import { actions as declineInvitationActions } from '../../../redux/programs/declineInvitation';

import styles from './styles.styl';

const formatDate = date => date && moment(date).format('MMM DD, YYYY');

function ProgramCard({ program, intl: { formatMessage } }) {
  const [confirm, setConfirm] = useState(false);

  const dispatch = useDispatch();
  const { id: userId, displayableName } = useSelector(targetUserSelector) || {};
  const mainUserId = useSelector(loggedUserIdSelector);

  const {
    logo = {},
    name: title,
    programType,
    description,
    organization,
    joinHistory = [],
    withSubscription,
    patientInvitation = {},
    subscriptionOption = {},
  } = {
    ...program,
  };

  const {
    handleChangeMembershipDuration,
    isSubscriptionStatusPending,
    moreThanOneSubscriptionOption,
    prepaidByProvider,
    selectedMembershipId,
    subscriptionsSetting,
  } = useProgramSubscription({ program });

  const { price, period, periodQuantity, recurrenceType } = {
    ...subscriptionsSetting,
  };

  const allowPrepay = get(subscriptionOption, 'prepayAllowed', false);
  const duePaymentDate = get(subscriptionOption, 'expiredAt', '');
  const autoRenewal = get(subscriptionOption, 'autoRenewal', false);
  const expiredStatus =
    get(subscriptionOption, 'status', '') === SUBSCRIPTION_STATUS.EXPIRED;

  const isOneTimePurchase =
    recurrenceType === MEMBERSHIP_TYPE.ONE_TIME_PURCHASE;

  const { name: subtitle, id: organizationId } = { ...organization };
  const { consents = [] } = { ...patientInvitation };
  const activeType = programType === PROGRAM_TYPE.ACTIVE;
  const inviteType = programType === PROGRAM_TYPE.INVITES;
  const historyType = programType === PROGRAM_TYPE.HISTORY;

  const { list, proxify } = useProgramConsents({
    program,
    consents,
    formatMessage,
  });

  const payload = {
    userId,
    organizationId,
    duePaymentDate,
    withSubscription,
    prepaidByProvider,
    programName: title,
    subscriptionsSetting,
    programId: get(program, 'id'),
    invitationId: get(patientInvitation, 'id'),
  };

  const icon = useMemo(
    () =>
      logo.thumbMin && (
        <SimpleAvatar
          size={32}
          text={title}
          className="mr2"
          src={logo.thumbMin}
        />
      ),
    [logo],
  );

  const note = useMemo(() => {
    return {
      [PROGRAM_TYPE.ACTIVE]: (
        <Fragment>
          {withSubscription &&
            duePaymentDate &&
            !autoRenewal &&
            `${formatMessage({
              id: 'program.membership.paymentDue',
            })} ${formatDate(duePaymentDate)}`}
        </Fragment>
      ),
      [PROGRAM_TYPE.HISTORY]: (
        <Fragment>
          {expiredStatus
            ? `${formatMessage({ id: 'common.expired' })} ${formatDate(
                get(subscriptionOption, 'expiredAt'),
              )}`
            : `${formatDate(get(joinHistory, '[0].joinedAt'))} - ${formatDate(
                get(joinHistory, '[0].removedAt'),
              )}`}
        </Fragment>
      ),
      [PROGRAM_TYPE.INVITES]: (
        <Fragment>
          <Button
            color="accent"
            className={styles.button}
            onClick={() => {
              dispatch(declineInvitationActions.init(payload));
            }}
          >
            {formatMessage({ id: 'common.decline' })}
          </Button>

          <Button
            color="primary"
            className={styles.button}
            disabled={moreThanOneSubscriptionOption && !selectedMembershipId}
            onClick={proxify(() => {
              dispatch(acceptInvitationActions.init(payload));
            })}
          >
            {formatMessage({
              id:
                withSubscription && !prepaidByProvider
                  ? 'program.membership.pay'
                  : 'common.join',
            })}
          </Button>
        </Fragment>
      ),
    }[programType];
  }, [
    proxify,
    programType,
    joinHistory,
    withSubscription,
    subscriptionOption,
    moreThanOneSubscriptionOption,
    selectedMembershipId,
  ]);

  const handleViewNextPayment = useMemo(() => {
    if (duePaymentDate && autoRenewal) return formatDate(duePaymentDate);
    return '-';
  }, [duePaymentDate]);

  const safeContent = useMemo(
    () =>
      mainUserId === userId
        ? formatMessage({ id: 'program.card.leave.body' })
        : formatMessage(
            { id: 'program.card.leave.body.ar' },
            { dependentName: displayableName },
          ),
    [mainUserId, userId, displayableName],
  );

  return (
    <ExpandableListItem
      opened={inviteType}
      locked={inviteType}
      className={styles.card}
      header={{
        note,
        title,
        subtitle,
        reverseTitle: true,
        icon: icon || 'program',
      }}
    >
      <If condition={inviteType}>
        <div className={styles.section}>
          <p className="f-12">
            <span className={styles.subtitle}>{subtitle} </span>
            {formatMessage(
              { id: 'program.invite.details.titleInvite' },
              { name: title },
            )}
          </p>

          <ProgramSubscription
            formatMessage={formatMessage}
            handleChangeMembershipDuration={handleChangeMembershipDuration}
            isSubscriptionStatusPending={isSubscriptionStatusPending}
            prepaidByProvider={prepaidByProvider}
            program={program}
            selectedMembershipId={selectedMembershipId}
            subscriptionsSetting={subscriptionsSetting}
          />
        </div>
      </If>

      <If condition={withSubscription && (activeType || historyType)}>
        <div className={styles.paidProgramInfo}>
          <div className={styles.columnWrapper}>
            <div className={styles.programCost}>
              {formatMessage({ id: 'program.membership.title' })}
            </div>
            <div className={styles.activeProgramCost}>
              {viewProgramCost({
                price,
                period,
                formatMessage,
                recurrenceType,
                quantity: periodQuantity,
              })}
            </div>
          </div>
          {!historyType && !isOneTimePurchase && (
            <div className={styles.columnWrapper}>
              <div className={styles.programCost}>
                {formatMessage({ id: 'program.membership.nextPayment' })}
              </div>
              <div className={styles.activeProgramCost}>
                {handleViewNextPayment}
              </div>
            </div>
          )}
        </div>
        {activeType && allowPrepay && (
          <Button
            raised
            color="primary"
            className={styles.prepayButton}
            onClick={proxify(() => {
              dispatch(
                acceptInvitationActions.init({
                  ...payload,
                  prepayFlow: true,
                }),
              );
            })}
          >
            {formatMessage({ id: 'program.membership.prepay' })}
          </Button>
        )}
      </If>

      <If condition={consents.length}>
        <div className={styles.section}>{list}</div>
      </If>

      <If condition={description}>
        <div className={styles.section}>
          <p className={styles.title}>
            {formatMessage({ id: 'program.invite.details.descriptionTitle' })}
          </p>

          <ShowMore
            minHeight={14 * 3}
            more={formatMessage({ id: 'common.readMore' })}
            less={formatMessage({ id: 'common.collapse' })}
          >
            <p className={styles.description}>{description}</p>
          </ShowMore>
        </div>
      </If>

      <If condition={activeType}>
        <div className={styles.section}>
          <Button border color="accent" onClick={() => setConfirm(true)}>
            {formatMessage({ id: 'program.card.leave' })}
          </Button>
        </div>
      </If>

      <If condition={historyType && expiredStatus}>
        <div className={styles.section}>
          <Button
            raised
            color="primary"
            className={styles.purchaseAgainButton}
            onClick={proxify(() => {
              dispatch(
                acceptInvitationActions.init({
                  ...payload,
                  purchaseAgain: true,
                }),
              );
            })}
          >
            {formatMessage({ id: 'program.membership.pay' })}
          </Button>
        </div>
      </If>

      <ActionConfirmation
        opened={confirm}
        onClose={() => setConfirm(false)}
        actionButtonText={formatMessage({ id: 'common.leave' })}
        title={formatMessage({ id: 'program.card.leave.title' })}
        content={safeContent}
        onSubmit={() => dispatch(leaveProgramActions.init(payload))}
      />
    </ExpandableListItem>
  );
}

ProgramCard.propTypes = {
  intl: PropTypes.object,
  program: PropTypes.object,
};

export default compose(injectIntl)(ProgramCard);



// WEBPACK FOOTER //
// ./src/components/Programs/ProgramCard/index.jsx